import { apiFetch } from "@/services/apiClient";
import { API_URL } from "@/lib/utils";

export interface RecoveryFilters {
  managers: string[];
  client_partners: string[];
  assignment_names: string[];
  service_lines: string[];
  legacy_location: string[];
  date_from: string | null;
  date_to: string | null;
}

export interface RecoveryFilterOptions {
  managers: string[];
  client_partners: string[];
  assignment_names: string[];
  service_lines: string[];
  legacy_location: string[];
  min_month: string | null;
  max_month: string | null;
}

function buildQs(f: RecoveryFilters): string {
  const p = new URLSearchParams();
  f.managers.forEach(v => p.append("managers", v));
  f.client_partners.forEach(v => p.append("client_partners", v));
  f.assignment_names.forEach(v => p.append("assignment_names", v));
  f.service_lines.forEach(v => p.append("service_lines", v));
  f.legacy_location.forEach(v => p.append("legacy_location", v));
  if (f.date_from) p.append("date_from", f.date_from);
  if (f.date_to)   p.append("date_to",   f.date_to);
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await apiFetch(`${API_URL}${path}`, { method: "GET", signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export const fetchRecoveryFilterOptions = (filters: RecoveryFilters = { managers: [], client_partners: [], assignment_names: [], service_lines: [], legacy_location: [], date_from: null, date_to: null }, signal?: AbortSignal) =>
  getJson<Omit<RecoveryFilterOptions, "min_month" | "max_month">>(`/recovery/filter-options${buildQs(filters)}`, signal);

export const fetchRecoveryDateRange = () =>
  getJson<{ min_month: string | null; max_month: string | null }>("/recovery/date-range");

export const fetchRecoveryChargedMonthly  = (f: RecoveryFilters, signal?: AbortSignal) =>
  getJson<{ data: { month_label: string; charged: number }[] }>(`/recovery/charged-monthly${buildQs(f)}`, signal);

export const fetchRecoveryBilledNetMonthly = (f: RecoveryFilters, signal?: AbortSignal) =>
  getJson<{ data: { month_label: string; billed_net: number }[] }>(`/recovery/billed-net-monthly${buildQs(f)}`, signal);

export const fetchRecoveryWriteOffMonthly  = (f: RecoveryFilters, signal?: AbortSignal) =>
  getJson<{ data: { month_label: string; write_off: number }[] }>(`/recovery/write-off-monthly${buildQs(f)}`, signal);

export const fetchRecoveryPctMonthly       = (f: RecoveryFilters, signal?: AbortSignal) =>
  getJson<{ data: { month_label: string; recovery_pct: number | null }[] }>(`/recovery/pct-monthly${buildQs(f)}`, signal);

export const fetchRecoveryPctByServiceLine = (f: RecoveryFilters, signal?: AbortSignal) =>
  getJson<{ data: Record<string, number | string | null>[]; series: string[] }>(`/recovery/pct-by-service-line${buildQs(f)}`, signal);

export const fetchRecoveryPctByLocation    = (f: RecoveryFilters, signal?: AbortSignal) =>
  getJson<{ data: Record<string, number | string | null>[]; series: string[] }>(`/recovery/pct-by-location${buildQs(f)}`, signal);

export const fetchRecoveryTopManagersWriteOff = (f: RecoveryFilters, limit = 10, signal?: AbortSignal) =>
  getJson<{ data: { manager: string; write_off: number }[] }>(`/recovery/top-managers-write-off${buildQs(f)}${buildQs(f) ? "&" : "?"}limit=${limit}`, signal);

export const fetchRecoveryTopAssignments = (f: RecoveryFilters, limit = 10, metric: "billed_net" | "write_off" | "charged" = "billed_net", signal?: AbortSignal) =>
  getJson<{ data: { assignment_name: string; billed_net?: number; write_off?: number; charged?: number }[]; metric: string }>(
    `/recovery/top-assignments${buildQs(f)}${buildQs(f) ? "&" : "?"}limit=${limit}&metric=${metric}`, signal);
